import React, { useState, useEffect } from 'react';
import axios from "axios";
import { Search, Edit2, Trash2, User, Mail, Phone, RefreshCw, Download, Filter, Users, ChevronRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const getRolePill = (role) => {
  const r = (role || '').toLowerCase();
  const map = {
    admin:     { bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.22)', color: '#A78BFA' },
    sales:     { bg: 'rgba(52,211,153,0.10)',  border: 'rgba(52,211,153,0.20)',  color: '#34D399' },
    operation: { bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.20)',  color: '#60A5FA' },
    customer:  { bg: 'var(--nd-w06)', border: 'var(--nd-w10)', color: '#94A3B8' },
  };
  const found = Object.entries(map).find(([k]) => r.includes(k));
  const style = found ? found[1] : { bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.20)', color: '#FBBF24' };
  return (
    <span style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em' }}>
      {role || 'User'}
    </span>
  );
};

function UserAvatar({ first = 'U', last = 'U' }) {
  const gradients = [['#3B82F6','#22D3A5'],['#8B5CF6','#3B82F6'],['#F472B6','#8B5CF6'],['#FBBF24','#F472B6']];
  const hash = (first.charCodeAt(0) + last.charCodeAt(0)) % gradients.length;
  const [c1, c2] = gradients[hash];
  return (
    <div style={{ width: 38, height: 38, minWidth: 38, borderRadius: '10px', background: `linear-gradient(135deg,${c1},${c2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nd-text-strong)', fontWeight: '800', fontSize: '13px', letterSpacing: '0.5px' }}>
      {first.charAt(0).toUpperCase()}{last.charAt(0).toUpperCase()}
    </div>
  );
}

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user`);
      setUsers(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch { setError("Failed to load users."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/user/${id}`);
      setUsers(p => p.filter(u => (u._id || u.id) !== id));
    } catch { alert("Failed to delete user."); }
  };

  const filteredUsers = users.filter(u => {
    const t = searchTerm.toLowerCase();
    return [u.Firstname, u.Lastname, u.Email, u.Username].some(v => v?.toLowerCase().includes(t));
  });

  const th = { padding: '12px 20px', fontSize: '10px', fontWeight: '700', color: 'var(--nd-tx25)', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'left', borderBottom: '1px solid var(--nd-w05)', background: 'var(--nd-w02)' };
  const td = { padding: '14px 20px', fontSize: '13px', color: '#94A3B8', borderBottom: '1px solid var(--nd-w04)' };

  return (
    <div style={{ padding: '14px 20px', fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes um-spin{to{transform:rotate(360deg)}} .um-row:hover td{background:var(--nd-w02)!important} .um-btn:hover{opacity:.80}`}</style>

      {/* Header */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', padding: '20px 24px 18px', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--nd-tx25)', marginBottom: '10px' }}>
          <span>Admin</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={{ color: 'rgba(34,211,165,0.55)' }}>User Management</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--nd-tx85)', letterSpacing: '-0.03em', lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Users size={22} color="#3B82F6"/>User Management
            </div>
            <div style={{ fontSize: '12px', color: 'var(--nd-tx30)', marginTop: '5px' }}>Manage system access and employee accounts</div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right', marginRight: '8px' }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '28px', fontWeight: '700', lineHeight: 1, background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{users.length}</div>
              <div style={{ fontSize: '11px', color: 'var(--nd-tx25)', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Users</div>
            </div>
            <button onClick={() => navigate('/admin/create-user')} style={{ padding: '10px 18px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', fontFamily: "'DM Sans',sans-serif" }}>
              <Plus size={15}/>Add User
            </button>
          </div>
        </div>
        <div style={{ height: '2px', borderRadius: '2px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      {/* Toolbar */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '12px', padding: '14px 18px', marginBottom: '14px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
          <Search size={15} color="var(--nd-tx22)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}/>
          <input type="text" placeholder="Search by name, email or username…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '10px 14px 10px 36px', background: 'var(--nd-w04)', border: '1px solid var(--nd-w08)', borderRadius: '9px', color: 'var(--nd-text)', fontSize: '13px', outline: 'none', fontFamily: "'DM Sans',sans-serif", colorScheme: 'dark' }}/>
        </div>
        {[
          { icon: <RefreshCw size={15} style={loading ? { animation: 'um-spin 1s linear infinite' } : {}}/>, fn: fetchUsers },
          { icon: <Filter size={15}/>, label: 'Filter' },
          { icon: <Download size={15}/>, label: 'Export' },
        ].map((btn, i) => (
          <button key={i} onClick={btn.fn} className="um-btn" style={{ padding: '9px 14px', background: 'var(--nd-w04)', border: '1px solid var(--nd-w08)', borderRadius: '9px', color: 'var(--nd-tx40)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',sans-serif" }}>
            {btn.icon}{btn.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', overflow: 'hidden' }}>
        {error && <div style={{ padding: '14px 20px', background: 'rgba(248,113,113,0.07)', borderBottom: '1px solid rgba(248,113,113,0.18)', color: '#F87171', fontSize: '13px', fontWeight: '600', display: 'flex', justifyContent: 'space-between' }}>{error}<button onClick={fetchUsers} style={{ background: 'none', border: 'none', color: '#F87171', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' }}>Retry</button></div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead><tr>
              <th style={th}>User</th><th style={th}>Contact</th><th style={th}>Role / Dept</th><th style={th}>Status</th><th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ padding: '60px', textAlign: 'center' }}>
                  <div style={{ width: '36px', height: '36px', border: '3px solid var(--nd-w08)', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'um-spin 1s linear infinite', margin: '0 auto' }}/>
                  <p style={{ color: 'var(--nd-tx25)', marginTop: '12px', fontSize: '13px' }}>Loading users…</p>
                </td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: '60px', textAlign: 'center', color: 'var(--nd-tx22)', fontSize: '13px' }}>{searchTerm ? `No results for "${searchTerm}"` : 'No users available'}</td></tr>
              ) : filteredUsers.map(user => (
                <tr key={user._id || user.id} className="um-row">
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                      <UserAvatar first={user.Firstname} last={user.Lastname}/>
                      <div>
                        <div style={{ fontWeight: '700', color: 'var(--nd-text)', fontSize: '14px' }}>{user.Firstname} {user.Lastname}</div>
                        <div style={{ color: 'var(--nd-tx30)', fontSize: '12px' }}>@{user.Username}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}><Mail size={13} color="var(--nd-tx25)"/>{user.Email || '—'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}><Phone size={13} color="var(--nd-tx25)"/>{user.MobileNumber || '—'}</div>
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                      {getRolePill(user.Type)}
                      <span style={{ fontSize: '11px', color: 'var(--nd-tx25)', marginLeft: '2px' }}>{user.Department || 'General'}</span>
                    </div>
                  </td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#34D399', background: 'rgba(52,211,153,0.09)', border: '1px solid rgba(52,211,153,0.18)', padding: '2px 9px', borderRadius: '12px' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#34D399' }}/>Active
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '7px' }}>
                      <button onClick={() => navigate(`/user/edit/${user._id || user.id}`)} style={{ padding: '7px', borderRadius: '7px', border: 'none', background: 'rgba(59,130,246,0.10)', color: '#60A5FA', cursor: 'pointer' }}><Edit2 size={15}/></button>
                      <button onClick={() => handleDelete(user._id || user.id)} style={{ padding: '7px', borderRadius: '7px', border: 'none', background: 'rgba(248,113,113,0.10)', color: '#F87171', cursor: 'pointer' }}><Trash2 size={15}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;